import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {TagModel} from '../../models/api/tagModel';
import {TagPill} from '../common/tags/tagPill';

/*
 * Props.
 */

interface ReviewTagItem {
  tag: TagModel;
  onSelect: () => void;
  isSelected: boolean;
  isHarvestingDone: boolean;
}

export interface AiReviewHarvestingSidebarProps {
  reviewItems: ReadonlyArray<ReviewTagItem>;
  isLoading: boolean;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.greys.shade20};
  overflow: auto;
`;

const StyledMainDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 22px;
  gap: 30px;
  flex-grow: 1;
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.huge};
  font-weight: ${fontWeights.black};
  margin: 0 8px;
`;

const StyledSectionNavigationDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledSectionDiv = styled.div`
  display: grid;
  align-content: center;
  grid-template-columns: 28px 1fr 20px;

  height: 34px;
  padding: 8px;
  border-radius: 6px;

  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  line-height: 19px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }

  &[aria-selected='true'] {
    background-color: ${(p) => p.theme.alphas.gray20};
  }
`;

const StyledTagPill = styled(TagPill)`
  ${flexCenter};
  margin-right: 9px;
`;

const StyledTagNameDiv = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: no-wrap;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  overflow: hidden;
`;

/*
 * Component.
 */

export const AiReviewHarvestingSidebar: FC<AiReviewHarvestingSidebarProps> = ({isLoading, reviewItems}) => (
  <StyledWrapperDiv>
    <StyledMainDiv>
      <StyledHeaderDiv>
        <Trans id="ai-review-harvesting-sidebar-title">Tags</Trans>
      </StyledHeaderDiv>
      <StyledSectionNavigationDiv>
        {isLoading
          ? renderLoadingState()
          : reviewItems.map((reviewItem) => (
              <ChromelessButton onClick={reviewItem.onSelect} key={reviewItem.tag.id}>
                <StyledSectionDiv aria-selected={reviewItem.isSelected}>
                  <StyledTagPill tag={reviewItem.tag} shouldHideName />
                  <StyledTooltipCoordinator render={() => reviewItem.tag.name}>
                    <StyledTagNameDiv>
                      <TooltipOverflowCondition>{reviewItem.tag.name}</TooltipOverflowCondition>
                    </StyledTagNameDiv>
                  </StyledTooltipCoordinator>
                  {reviewItem.isHarvestingDone && (
                    <UserThemeConsumer>
                      {(theme) => <Icon name="checkmarkCircleSmall" color={theme.palette.green.shade40} />}
                    </UserThemeConsumer>
                  )}
                </StyledSectionDiv>
              </ChromelessButton>
            ))}
      </StyledSectionNavigationDiv>
    </StyledMainDiv>
  </StyledWrapperDiv>
);

function renderLoadingState() {
  return (
    <>
      <Skeleton width="100%" height="32px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY30} />
      <Skeleton width="100%" height="32px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY30} />
      <Skeleton width="100%" height="32px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY30} />
    </>
  );
}
